(() => {
  'use strict';
  const FIT_MODEL = {"coefficients":{"bust":[-5.104678024,-0.030510448,1.551425597],"hips":[8.035385254,0.102788225,1.467197406]},"fuller":{"bust":2.8133,"hips":1.9647,"navel":3.1298}};
  function bodyEstimate(answers) {
    const height = Number(answers.height) * (answers.heightUnit === 'in' ? 2.54 : 1);
    const weight = Number(answers.weight) * (answers.weightUnit === 'lb' ? 0.45359237 : 1);
    if (!answers.height || !answers.weight || !Number.isFinite(height) || !Number.isFinite(weight) || height < 120 || height > 220 || weight < 30 || weight > 250) return null;
    const features = [1, height - 165, 100 * Math.sqrt(weight / height)];
    const predict = key => FIT_MODEL.coefficients[key].reduce((n,c,i) => n + c * features[i], 0);
    const shapes = new Set(answers.shapes || []);
    const fuller = key => shapes.has('all') || shapes.has(key);
    return {height,weight,bust:predict('bust'),hips:predict('hips'),bustDelta:fuller('bust') ? FIT_MODEL.fuller.bust : 0,hipDelta:fuller('hips') ? FIT_MODEL.fuller.hips : 0,waistDelta:fuller('waist') ? FIT_MODEL.fuller.navel : 0};
  }
  function fitResult(profile, answers) {
    if (profile.kind === 'one_size') return {size:profile.sizes[0],reason:'one'};
    const body = bodyEstimate(answers);
    if (!body || !profile.fit) return {size:null,reason:'invalid'};
    const bottom = profile.kind.includes('bottom');
    const full = profile.kind === 'dress' || profile.kind === 'swim_full';
    const centers = profile.fit.centers;
    const demands = [];
    if (!bottom) demands.push(['bust',body.bust + body.bustDelta]);
    if (bottom || full) demands.push(['hips',body.hips + body.hipDelta]);
    if (profile.fit.waistSensitive && body.waistDelta) {
      const base = bottom ? body.hips - 96 : full ? Math.max(body.bust - 90,body.hips - 96) : body.bust - 90;
      demands.push(['waist',72 + base + body.waistDelta]);
    }
    const last = profile.sizes.length - 1;
    function boundary(key,index) {
      const values=centers[key];
      if (index < last) return (values[index] + values[index + 1]) / 2;
      return values[index] + (values[index] - values[index - 1]) * (profile.fit.relaxed ? 1 : .5);
    }
    const index = profile.sizes.findIndex((_,i) => demands.every(([key,value]) => value <= boundary(key,i)));
    if (index < 0) return {size:null,reason:'outside',body};
    const below = index === 0 && demands.every(([key,value]) => value < centers[key][0] - (centers[key][1] - centers[key][0]) / 2);
    return {size:profile.sizes[index],reason:below ? 'smallest' : 'estimate',body};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {fitResult,bodyEstimate};

  if (typeof window === 'undefined') return;
  if (customElements.get('basiic-fit-guide')) return;
  class BasiicFitGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-fit-form]');
      this.profile = JSON.parse(this.querySelector('[data-fit-profile]').textContent);
      this.product = JSON.parse(this.querySelector('[data-fit-product]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-fit-strings]').textContent);
      this.root = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.root.querySelectorAll('[data-fit-open]').forEach(button => {
        button.addEventListener('click', () => {
          this.opener = button;
          this.setView(button.dataset.fitOpenView || 'quiz');
          if (!this.dialog.open) this.dialog.showModal();
          this.previousOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          this.focusHeading();
        }, {signal});
      });
      this.querySelector('[data-fit-close]').addEventListener('click', () => this.dialog.close(), {signal});
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.opener?.focus();
      }, {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, {signal});
      this.querySelectorAll('[data-fit-view]').forEach(button => button.addEventListener('click', () => {
        this.setView(button.dataset.fitView);
        this.focusHeading();
      }, {signal}));
      this.querySelector('[data-fit-next]').addEventListener('click', () => {
        if (this.valid()) this.step(2);
      }, {signal});
      this.querySelector('[data-fit-back]').addEventListener('click', () => this.step(1), {signal});
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.currentStep !== 2) {
          if (this.valid()) this.step(2);
          return;
        }
        if (!this.valid()) return;
        if (!this.form.querySelector('[name="shape"]:checked')) {
          this.error('empty_shape');
          return;
        }
        this.showResult();
      }, {signal});
      this.form.addEventListener('change', event => {
        if (event.target.name === 'shape') {
          const single = ['all', 'slim', 'shape_unsure'];
          if (event.target.checked) this.form.querySelectorAll('[name="shape"]').forEach(input => {
            if (input !== event.target && (single.includes(event.target.value) || single.includes(input.value))) input.checked = false;
          });
        }
        this.clearError();
        this.updateContact();
      }, {signal});
      this.form.addEventListener('input', () => this.updateContact(), {signal});
      this.querySelector('[data-fit-restart]').addEventListener('click', () => {
        this.form.hidden = false;
        this.querySelector('[data-fit-result]').hidden = true;
        this.result = null;
        this.step(1);
        this.updateContact();
      }, {signal});
      this.querySelector('[data-fit-select]').addEventListener('click', () => this.selectSize(), {signal});
      this.querySelector('[data-fit-chart-unit]').addEventListener('change', event => this.convert(event.target.value), {signal});
      this.root.querySelector('variant-picker')?.addEventListener('change', () => {
        if (this.result?.size) this.updateStock();
      }, {signal});
      const kind = this.profile.kind;
      if (kind === 'one_size') {
        this.form.hidden = true;
        this.querySelector('[data-fit-one]').hidden = false;
        this.querySelector('.bfit-unit-row').hidden = true;
      }
      if (!this.profile.chart) this.querySelector('.bfit-unit-row').hidden = true;
      this.currentStep = 1;
      this.updateContact();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.ready = false;
    }
    t(key, values = {}) {
      return Object.entries(values).reduce((out, [k, v]) => out.replaceAll(`__${k}__`, String(v)).replaceAll(`{{ ${k} }}`, String(v)).replaceAll(`{{${k}}}`, String(v)), this.strings[key] || key);
    }
    answers() {
      const values = new FormData(this.form);
      return {shapes: values.getAll('shape'), height: values.get('height'), heightUnit: values.get('height_unit'), weight: values.get('weight'), weightUnit: values.get('weight_unit')};
    }
    valid() {
      const a = this.answers();
      const height = Number(a.height) * (a.heightUnit === 'in' ? 2.54 : 1);
      const weight = Number(a.weight) * (a.weightUnit === 'lb' ? 0.45359237 : 1);
      if (this.form.elements.height.validity.badInput || (a.height === '' || !Number.isFinite(height) || height < 120 || height > 220)) {this.error('invalid_height', this.form.elements.height);return false;}
      if (this.form.elements.weight.validity.badInput || (a.weight === '' || !Number.isFinite(weight) || weight < 30 || weight > 250)) {this.error('invalid_weight', this.form.elements.weight);return false;}
      this.clearError();
      return true;
    }
    error(key, input) {
      const el = this.querySelector('[data-fit-error]');
      el.textContent = this.t(key);
      el.hidden = false;
      if (input) {input.setAttribute('aria-invalid', 'true');input.closest('details')?.setAttribute('open', '');input.focus();}
    }
    clearError() {
      this.querySelector('[data-fit-error]').hidden = true;
      this.form.querySelectorAll('[aria-invalid]').forEach(el => el.removeAttribute('aria-invalid'));
    }
    step(number) {
      this.currentStep = number;
      this.querySelectorAll('[data-fit-step]').forEach(el => el.hidden = Number(el.dataset.fitStep) !== number);
      this.clearError();
      this.focusHeading();
      this.querySelector('.bfit-body').scrollTop = 0;
    }
    setView(view) {
      this.view = view;
      this.querySelectorAll('[data-fit-panel]').forEach(el => el.hidden = el.dataset.fitPanel !== view);
      this.querySelectorAll('.bfit-nav [data-fit-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fitView === view)));
      this.querySelector('.bfit-body').scrollTop = 0;
    }
    focusHeading() {
      this.dialog.scrollTop = 0;
      const panel = this.querySelector(`[data-fit-panel="${this.view || 'quiz'}"]`);
      const heading = [...panel.querySelectorAll('[tabindex="-1"], .bfit-table-scroll')].find(el => !el.closest('[hidden]'));
      (heading || this.querySelector('[data-fit-close]')).focus({preventScroll:true});
      this.dialog.scrollTop = 0;
    }
    showResult() {
      this.result = fitResult(this.profile, this.answers());
      const r = this.result;
      this.form.hidden = true;
      this.querySelector('[data-fit-result]').hidden = false;
      this.querySelector('[data-fit-result-label]').textContent = this.t('result_label');
      this.querySelector('[data-fit-result-label]').hidden = !r.size;
      this.querySelector('[data-fit-result-size]').textContent = r.size ? (Number(r.size) ? `AU ${r.size}` : r.size) : this.t('outside');
      this.querySelector('[data-fit-result-copy]').textContent = this.t(!r.size ? (r.reason === 'invalid' ? 'invalid_copy' : 'outside_copy') : r.reason === 'smallest' ? 'smallest_copy' : this.profile.fit?.basis === 'brand_reference' ? 'reference_basis' : 'result_basis');
      this.querySelectorAll('[data-fit-size]').forEach(row => {
        row.classList.toggle('bfit-highlight', row.dataset.fitSize === r.size);
        if (row.dataset.fitSize === r.size) row.setAttribute('aria-current', 'true'); else row.removeAttribute('aria-current');
      });
      this.updateStock();
      this.updateContact();
      this.focusHeading();
      this.querySelector('.bfit-body').scrollTop = 0;
    }
    matchingVariant() {
      if (!this.result?.size) return null;
      const index = this.product.options.findIndex(name => name.toLowerCase() === 'size');
      const picker = this.root.querySelector('variant-picker');
      const selected = picker ? [...picker.querySelectorAll('fieldset')].map(fs => fs.querySelector('input:checked')?.value) : [];
      return this.product.variants.find(v => v.options[index] === this.result.size && v.options.every((option, i) => i === index || !selected[i] || option === selected[i]));
    }
    updateStock() {
      const variant = this.matchingVariant();
      const button = this.querySelector('[data-fit-select]');
      const stock = this.querySelector('[data-fit-stock]');
      button.hidden = !variant || !variant.available;
      button.disabled = !variant || !variant.available;
      stock.hidden = !this.result?.size || Boolean(variant?.available);
      stock.textContent = this.result?.size ? this.t('sold_out', {size:this.result.size}) : '';
      button.textContent = this.result?.size ? this.t('select_size', {size:this.result.size}) : '';
    }
    selectSize() {
      const variant = this.matchingVariant();
      if (!variant?.available) {this.updateStock();return;}
      const picker = this.root.querySelector('variant-picker');
      const index = this.product.options.findIndex(name => name.toLowerCase() === 'size');
      const fieldset = picker?.querySelectorAll('fieldset')[index];
      const input = [...(fieldset?.querySelectorAll('input') || [])].find(input => input.value === this.result.size);
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', {bubbles:true}));
      this.dialog.close();
    }
    convert(unit) {
      this.querySelectorAll('[data-cm]').forEach(cell => {
        const cm = Number(cell.dataset.cm);
        cell.textContent = unit === 'in' ? (cm / 2.54).toFixed(1) : String(cm);
      });
      this.querySelectorAll('[data-fit-unit-label]').forEach(el => el.textContent = unit === 'in' ? this.t('inches') : this.t('cm'));
    }
    updateContact() {
      const a = this.answers();
      const rows = [this.product.title, this.product.url, '', this.t('contact_details')];
      if (a.shapes.length) rows.push(`${this.t('details_shape')}: ${a.shapes.map(s => this.t(s)).join(', ')}`);
      if (a.height) rows.push(`${this.t('details_height')}: ${a.height} ${a.heightUnit}`);
      if (a.weight) rows.push(`${this.t('details_weight')}: ${a.weight} ${a.weightUnit}`);
      if (this.result?.size) rows.push(`${this.t('details_result')}: ${this.result.size}`);
      this.querySelector('[data-fit-contact]').href = `mailto:info@basiic.com.au?subject=${encodeURIComponent(this.t('email_subject', {title:this.product.title}))}&body=${encodeURIComponent(rows.join('\n'))}`;
    }
  }
  customElements.define('basiic-fit-guide', BasiicFitGuide);
})();
